/**
 * @fileoverview Local-first system user service backed by system Dexie.
 * @module system-web/services/systemUserService
 *
 * Responsibilities:
 * - Read system administrator users from `firesquirrel-system`.
 * - Create new system users local-first, then send their invitation in the
 *   background (mirrors tenant-web's department-user invite flow).
 * - Persist normal user preference/edit changes locally before background sync.
 * - Provide send, resend, revoke, and refresh invitation lifecycle management
 *   (single and bulk) matching the tenant-web user invite process.
 *
 * Dependencies:
 * - @firesquirrel/local-db (Dexie, generateObjectId, wakeLocalWrite)
 * - dexie (liveQuery)
 * - ./invitationService (online-only invitation delivery)
 * - system audit logging
 *
 * Architecture:
 * - System-web user reads/writes use Dexie as the UI source of truth.
 * - Creating a user writes to `system_users` first and wakes sync; the
 *   invitation email is the only online step and runs in the background with
 *   retry to absorb the Dexie-write → sync-push → server-invite race.
 * - Invitation delivery, resend, revoke, and status checks are explicit
 *   inherently-online exceptions (same pattern as tenant-web userService).
 *
 * Notes:
 * - No server-first create path. The user appears in the grid immediately from
 *   Dexie; invite delivery is reported asynchronously via a callback.
 */

import { getSystemDb, wakeLocalWrite, generateObjectId } from '@firesquirrel/local-db';
import { liveQuery } from 'dexie';
import { api } from '../apiClient';
import {
  inviteUser as postSystemInvitation,
  resendSystemInvitation as postResendSystemInvitation,
  sendInvitationCommand as postInvitationCommand,
  revokeSystemInvitation as postRevokeSystemInvitation,
  generateSystemInviteLink as postGenerateInviteLink,
} from './invitationService';
import { computeAuditChanges, writeAuditLog } from './systemAuditLogService';

function isSystemAdministratorUser(doc: any): boolean {
  if (!doc || doc?._deleted === true) return false;
  const systemRoleName = String(doc?.systemRole?.roleName || '').trim();
  const systemRoleId = doc?.systemRole?.roleId ?? doc?.systemRoleId;
  return Boolean(systemRoleName || systemRoleId);
}

export async function listSystemUsers(): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.system_users.toArray();
  return docs.filter(isSystemAdministratorUser);
}

export async function getSystemUser(id: string): Promise<any | null> {
  const db = await getSystemDb();
  const doc = await db.system_users.get(id);
  return isSystemAdministratorUser(doc) ? doc : null;
}

export function watchSystemUsers(
  onNext: (users: any[]) => void,
  onError?: (error: unknown) => void,
): () => void {
  let subscription: { unsubscribe: () => void } | null = null;
  let cancelled = false;

  getSystemDb()
    .then((db) => {
      if (cancelled) return;
      subscription = liveQuery(async () => {
        const docs = await db.system_users.toArray();
        return docs.filter(isSystemAdministratorUser);
      }).subscribe({
        next: (users) => onNext(users),
        error: (error) => { onError?.(error); },
      });
    })
    .catch((error) => {
      if (!cancelled) onError?.(error);
    });

  return () => {
    cancelled = true;
    subscription?.unsubscribe();
  };
}

export async function upsertSystemUser(data: any): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(data._id);
  const isNew = !existing;
  const next = { ...data, updatedAt: new Date().toISOString() };
  await db.system_users.put(next);
  wakeLocalWrite('system_users');
  const changes = isNew ? [] : computeAuditChanges(existing as Record<string, any>, next as Record<string, any>);
  if (isNew || changes.length > 0) {
    writeAuditLog({
      collection: 'users',
      documentId: data._id,
      action: isNew ? 'create' : 'update',
      summary: isNew ? `Created user "${next.email}"` : `Updated user "${next.email}"`,
      changes,
    }).catch((err) => {
      console.warn('[systemUserService] Failed to write audit log for user upsert:', err);
    });
  }
}

/**
 * @process UpdateSystemUserThemePreference
 *
 * Steps:
 * 1. Load the system user from local Dexie.
 * 2. Merge the theme override into `preferences.themeId`.
 * 3. Write locally and wake system sync.
 *
 * Inputs:
 * - id: System user id.
 * - themeId: Theme override id or null/undefined to clear the override.
 *
 * Outputs:
 * - Updated local user document or null when absent.
 *
 * Failure Handling:
 * - Dexie errors propagate to the caller.
 *
 * Idempotency:
 * - REQUIRED
 */
export async function updateSystemUserThemePreference(id: string, themeId?: string | null): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(id);
  if (!existing) return null;

  const next = {
    ...existing,
    preferences: {
      ...((existing as any).preferences ?? {}),
      themeId: themeId && themeId.trim() ? themeId.trim() : null,
    },
    updatedAt: new Date().toISOString(),
  };
  await db.system_users.put(next);
  wakeLocalWrite('system_users');
  return next;
}

function buildSystemUserPayload(data: any): any {
  const email = String(data?.email ?? '').trim();
  const firstName = String(data?.firstName ?? '').trim();
  const lastName = String(data?.lastName ?? '').trim();
  const title = String(data?.title ?? '').trim();
  const position = String(data?.position ?? '').trim();
  const status = String(data?.status ?? 'active').trim() || 'active';
  const roleId = String(data?.systemRole?.roleId ?? data?.systemRoleId ?? '').trim();
  const roleName = String(data?.systemRole?.roleName ?? '').trim();

  return {
    email,
    firstName,
    lastName,
    status,
    title: title || undefined,
    position: position || undefined,
    systemRole: roleId
      ? {
          roleId,
          roleName,
        }
      : null,
  };
}

const INVITE_RETRY_MAX_ATTEMPTS = 8;
const INVITE_RETRY_DELAY_MS = 1500;

/**
 * @contract BackgroundInviteResult
 * Purpose: Outcome of the background invitation send, surfaced to the UI.
 * Fields:  ok, user (on success), error (on failure), attempts.
 * Rules:   `ok === true` ⇒ `user` present; `ok === false` ⇒ `error` present.
 * Sync:    Reports the result of the online invite step that follows the local write.
 */
export interface BackgroundInviteResult {
  ok: boolean;
  user?: any;
  error?: string;
  attempts: number;
}

/**
 * @contract InviteSystemUserParams
 * Purpose: Inputs for creating a system user local-first and inviting them.
 * Fields:  email, firstName, lastName, title?, position?, roleId, roleName,
 *          inviteMessage?, inviteImmediately?, onBackgroundInviteResult?.
 * Rules:   roleId/roleName identify the platform role to assign on the server.
 * Sync:    The user is written to Dexie first; the invite is sent in background.
 */
export interface InviteSystemUserParams {
  email: string;
  firstName: string;
  lastName: string;
  title?: string;
  position?: string;
  roleId: string;
  roleName: string;
  inviteMessage?: string;
  inviteImmediately?: boolean;
  onBackgroundInviteResult?: (result: BackgroundInviteResult) => void;
}

function buildLocalSystemUser(params: InviteSystemUserParams): any {
  const now = new Date().toISOString();
  return {
    _id: generateObjectId(),
    email: params.email.trim(),
    firstName: params.firstName.trim(),
    lastName: params.lastName.trim(),
    title: params.title?.trim() || undefined,
    position: params.position?.trim() || undefined,
    status: 'invited',
    systemRole: {
      roleId: params.roleId,
      roleName: params.roleName,
    },
    identities: [],
    phoneNumbers: [],
    departmentMemberships: [],
    organizationMemberships: [],
    createdAt: now,
    updatedAt: now,
  };
}

function mergeInvitedSystemUser(localUser: any, apiResponse: any): any {
  if (!apiResponse) return localUser;
  const serverRole = apiResponse.systemRole;
  return {
    ...localUser,
    status: apiResponse.status ?? localUser.status,
    invite: apiResponse.invite ?? localUser.invite,
    invitationHistory: apiResponse.invitationHistory ?? localUser.invitationHistory,
    systemRole: serverRole
      ? {
          ...localUser.systemRole,
          roleId: String(serverRole.roleId ?? localUser.systemRole.roleId),
          roleName: String(serverRole.roleName ?? localUser.systemRole.roleName),
        }
      : localUser.systemRole,
    lastLoginAt: apiResponse.lastLoginAt ?? localUser.lastLoginAt,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * @process sendSystemInvitationInBackgroundWithRetry
 *
 * Steps:
 * 1. POST the invitation for the (now local) user.
 * 2. On success, merge the server's authoritative fields back into Dexie.
 * 3. On a sync-race error ("has not finished syncing"), wait and retry.
 * 4. On any other error, stop immediately and report the failure.
 *
 * Inputs:
 * - localUser: The Dexie user document already written.
 * - params: The original invite parameters.
 * - onResult: Optional callback receiving the final BackgroundInviteResult.
 *
 * Outputs:
 * - None (void). Communicates via Dexie writes and the onResult callback.
 *
 * Failure Handling:
 * - Retries only the sync race up to INVITE_RETRY_MAX_ATTEMPTS; non-retryable
 *   errors and exhaustion both report ok=false via onResult.
 *
 * Idempotency:
 * - REQUIRED — the server resolves the user by id/email; repeat sends update
 *   the same record.
 */
async function sendSystemInvitationInBackgroundWithRetry(
  localUser: any,
  params: InviteSystemUserParams,
  onResult?: (result: BackgroundInviteResult) => void,
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= INVITE_RETRY_MAX_ATTEMPTS; attempt += 1) {
    try {
      const apiResponse = await postSystemInvitation({
        userId: localUser._id,
        email: localUser.email,
        firstName: localUser.firstName,
        lastName: localUser.lastName,
        roleId: params.roleId,
        roleName: params.roleName,
        inviteMessage: params.inviteMessage,
        sendInvite: true,
      });
      const merged = mergeInvitedSystemUser(localUser, apiResponse);
      await upsertSystemUser(merged);
      onResult?.({ ok: true, user: merged, attempts: attempt });
      return;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      const isSyncRace = /has not finished syncing/i.test(lastError.message);
      if (!isSyncRace) {
        console.error('[systemUserService] background invite failed (non-retryable):', lastError);
        onResult?.({ ok: false, error: lastError.message, attempts: attempt });
        return;
      }
      console.warn(
        `[systemUserService] background invite attempt ${attempt}/${INVITE_RETRY_MAX_ATTEMPTS} hit sync race — retrying in ${INVITE_RETRY_DELAY_MS}ms`,
      );
      if (attempt < INVITE_RETRY_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, INVITE_RETRY_DELAY_MS));
      }
    }
  }

  console.error('[systemUserService] background invite failed after max retries:', lastError);
  onResult?.({
    ok: false,
    error:
      'The user was added, but the invitation email could not be sent yet because the system database is still syncing. '
      + 'Use "Resend Invitation" on the user row to retry.',
    attempts: INVITE_RETRY_MAX_ATTEMPTS,
  });
}

/**
 * @process inviteSystemUser
 *
 * Steps:
 * 1. Build the new user document with a client-generated ObjectId.
 * 2. Write it to Dexie (`system_users`) and wake sync — UI updates instantly.
 * 3. Unless suppressed, fire the background invitation send (with retry).
 *
 * Inputs:
 * - params: InviteSystemUserParams (identity, role, optional message/callback).
 *
 * Outputs:
 * - The locally-created user document (returned synchronously after the write).
 *
 * Failure Handling:
 * - The Dexie write rejects to the caller; invitation delivery never blocks the
 *   return and reports asynchronously via params.onBackgroundInviteResult.
 *
 * Idempotency:
 * - NOT REQUIRED — each call mints a new user id.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`).
 * Write Flow:      Dexie put → wakeLocalWrite('system_users') → sync push.
 * Sync Strategy:   Background invite POST after the record syncs to the server.
 * Conflict Resolution: server response merged back into the same Dexie record.
 */
export async function inviteSystemUser(params: InviteSystemUserParams): Promise<any> {
  const localUser = buildLocalSystemUser(params);

  await upsertSystemUser(localUser);

  if (params.inviteImmediately !== false) {
    void sendSystemInvitationInBackgroundWithRetry(localUser, params, params.onBackgroundInviteResult);
  }

  return localUser;
}

function mergeResentSystemUser(localUser: any, apiResponse: any): any {
  if (!apiResponse) return localUser;
  return {
    ...localUser,
    status: apiResponse.status ?? localUser.status,
    invite: apiResponse.invite ?? localUser.invite,
    invitationHistory: apiResponse.invitationHistory ?? localUser.invitationHistory,
    systemRole: apiResponse.systemRole ?? localUser.systemRole,
    lastLoginAt: apiResponse.lastLoginAt ?? localUser.lastLoginAt,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * @process resendSystemInvitation
 *
 * Steps:
 * 1. POST the resend for the (already-synced) system user.
 * 2. Merge the server's authoritative invite/status fields back into Dexie.
 * 3. Wake sync and report the outcome.
 *
 * Inputs:
 * - user: The system user whose invite should be resent (must have _id).
 * - onResult: Optional callback receiving the BackgroundInviteResult.
 *
 * Outputs:
 * - The BackgroundInviteResult (also delivered via onResult).
 *
 * Failure Handling:
 * - The server error message is surfaced via the result; the local record is
 *   left untouched on failure.
 *
 * Idempotency:
 * - NOT REQUIRED — each call mints a fresh invite + email on the server.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`); the server owns invite delivery.
 * Write Flow:      resend POST → merge server fields → Dexie put → wakeLocalWrite.
 * Sync Strategy:   Resend is the online step; the merged record then syncs back.
 * Conflict Resolution: server response merged into the same Dexie record.
 */
export async function resendSystemInvitation(
  user: any,
  onResult?: (result: BackgroundInviteResult) => void,
): Promise<BackgroundInviteResult> {
  try {
    const apiResponse = await postResendSystemInvitation(user._id);
    const db = await getSystemDb();
    const existing = await db.system_users.get(user._id);
    const merged = mergeResentSystemUser(existing ?? user, apiResponse);
    await db.system_users.put(merged);
    wakeLocalWrite('system_users');
    const result: BackgroundInviteResult = { ok: true, user: merged, attempts: 1 };
    onResult?.(result);
    return result;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    console.error('[systemUserService] resend invitation failed:', message);
    const result: BackgroundInviteResult = { ok: false, error: message, attempts: 1 };
    onResult?.(result);
    return result;
  }
}

export async function saveSystemUser(data: any): Promise<any> {
  const payload = buildSystemUserPayload(data);

  if (!data?._id) {
    return inviteSystemUser({
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      title: payload.title,
      position: payload.position,
      roleId: payload.systemRole?.roleId ?? '',
      roleName: payload.systemRole?.roleName ?? '',
    });
  }

  const next = {
    ...data,
    ...payload,
    _id: data._id,
  };

  await upsertSystemUser(next);

  api.users.update(data._id, payload)
    .then(async (serverUser) => {
      if (serverUser?._id) {
        await upsertSystemUser(serverUser);
      }
    })
    .catch((err) => {
      console.warn('[systemUserService] Failed to sync user update to server:', err);
    });

  return next;
}

export async function updateSystemUserStatus(user: any, status: string): Promise<any> {
  const next = {
    ...user,
    status,
  };

  await upsertSystemUser(next);

  api.users.updateStatus(user._id, status)
    .then(async (serverUser) => {
      if (serverUser?._id) {
        await upsertSystemUser(serverUser);
      }
    })
    .catch((err) => {
      console.warn('[systemUserService] Failed to sync user status update to server:', err);
    });

  return next;
}

export async function getSystemUserLoginHistory(id: string, params?: Record<string, string>): Promise<any> {
  return api.users.getLoginHistory(id, params);
}

export async function listSystemUserGroups(): Promise<any[]> {
  const db = await getSystemDb();
  const docs = await db.system_user_groups.toArray();
  return docs;
}

export async function upsertSystemUserGroup(data: any): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_user_groups.get(data._id);
  const isNew = !existing;
  const next = { ...data, updatedAt: new Date().toISOString() };
  await db.system_user_groups.put(next);
  wakeLocalWrite('system_user_groups');
  const changes = isNew ? [] : computeAuditChanges(existing as Record<string, any>, next as Record<string, any>);
  if (isNew || changes.length > 0) {
    writeAuditLog({
      collection: 'userGroups',
      documentId: data._id,
      action: isNew ? 'create' : 'update',
      summary: isNew ? `Created user group "${next.name}"` : `Updated user group "${next.name}"`,
      changes,
    }).catch((err) => {
      console.warn('[systemUserService] Failed to write audit log for user group upsert:', err);
    });
  }
}

// ============================================================
// Invitation Lifecycle — Send / Resend / Revoke / Refresh / Link
// (Mirrors tenant-web userService invitation lifecycle pattern)
// ============================================================

function toErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Merge server response into the existing Dexie system user record,
 * preserving any local-only fields while updating invitation state.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`); the server owns invite fields.
 * Write Flow:      server response → merge → Dexie put → wakeLocalWrite.
 * Sync Strategy:   Invitation actions are online; merged record then syncs back.
 * Conflict Resolution: server response wins for invite/status fields.
 */
function mergeServerResponseIntoLocal(existing: any, apiResponse: any): any {
  if (!apiResponse) return existing;
  return {
    ...existing,
    status: apiResponse.status ?? existing.status,
    invite: apiResponse.invite ?? existing.invite,
    invitationHistory: apiResponse.invitationHistory ?? existing.invitationHistory,
    systemRole: apiResponse.systemRole ?? existing.systemRole,
    lastLoginAt: apiResponse.lastLoginAt ?? existing.lastLoginAt,
    identities: apiResponse.identities ?? existing.identities,
    entraUserId: apiResponse.entraUserId ?? existing.entraUserId,
    updatedAt: new Date().toISOString(),
  };
}

/**
 * Derive a user-facing status from the system user's invitation state.
 * Mirrors tenant-web mapInviteStatusToUserStatus logic.
 */
export function deriveUserStatus(user: any): 'active' | 'suspended' | 'invited' | 'not_invited' | 'deleted' {
  if (user.status === 'deleted') return 'deleted';
  if (user.status === 'suspended') return 'suspended';
  if (user.status === 'active' && user.lastLoginAt) return 'active';
  const invite = user.invite;
  if (!invite) return user.status === 'invited' ? 'invited' : 'not_invited';
  const invStatus = invite.status ?? '';
  if (['revoked', 'expired', 'superseded'].includes(invStatus)) return 'not_invited';
  if (invStatus === 'accepted') return 'active';
  if (invStatus === 'sent' || invStatus === 'pending') return 'invited';
  return user.status || 'not_invited';
}

/**
 * @process sendSystemUserInvitation
 *
 * Steps:
 * 1. Load the system user from local Dexie.
 * 2. POST the invitation command (send or resend, depending on existing invite state).
 * 3. Merge the server response back into Dexie.
 *
 * Inputs:
 * - userId: The system user id.
 * - inviteMessage: Optional invite note.
 *
 * Outputs:
 * - The updated local user record, or null when absent.
 *
 * Failure Handling:
 * - Rejects with the server error; callers surface it to the UI.
 *
 * Idempotency:
 * - REQUIRED — the server resolves the user and updates the existing record.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`).
 * Write Flow:      API call → merge server fields → Dexie put → wakeLocalWrite.
 * Sync Strategy:   The send is the online step; the merged record then syncs.
 * Conflict Resolution: server response wins for invite state.
 */
export async function sendSystemUserInvitation(
  userId: string,
  inviteMessage?: string,
): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(userId);
  if (!existing) return null;

  const invite = (existing as any).invite;
  const shouldResend = !!invite && ['pending', 'sent', 'expired'].includes(invite.status ?? '');
  const action = shouldResend ? 'resend' : 'send';

  try {
    const apiResponse = await postInvitationCommand(userId, { action, inviteMessage });
    const merged = mergeServerResponseIntoLocal(existing, apiResponse);
    await db.system_users.put(merged);
    wakeLocalWrite('system_users');
    return merged;
  } catch (err) {
    console.error('[systemUserService] sendSystemUserInvitation failed:', err);
    throw new Error(toErrorMessage(err, 'Failed to send invitation'));
  }
}

/**
 * @process revokeSystemUserInvitation
 *
 * Steps:
 * 1. Load the system user from local Dexie.
 * 2. POST revoke to the server (no membership scope = system admin invite).
 * 3. Merge the server response back into Dexie.
 *
 * Inputs:  userId.
 * Outputs: The updated local user record, or null when absent.
 * Failure Handling: Rejects with the server error; callers surface it.
 * Idempotency: REQUIRED — revoking an already-revoked invite is a no-op.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`).
 * Write Flow:      API call → merge → Dexie put → wakeLocalWrite.
 * Sync Strategy:   Revoke is the online step; the merged record then syncs.
 * Conflict Resolution: server response wins for invite state.
 */
export async function revokeSystemUserInvitation(userId: string): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(userId);
  if (!existing) return null;

  try {
    const apiResponse = await postRevokeSystemInvitation(userId);
    const merged = mergeServerResponseIntoLocal(existing, apiResponse);
    await db.system_users.put(merged);
    wakeLocalWrite('system_users');
    return merged;
  } catch (err) {
    console.error('[systemUserService] revokeSystemUserInvitation failed:', err);
    throw new Error(toErrorMessage(err, 'Failed to revoke invitation'));
  }
}

/**
 * @process refreshSystemUserInviteStatus
 *
 * Steps:
 * 1. Load the system user from local Dexie.
 * 2. GET the latest user record from the server (online-only refresh).
 * 3. Merge the server response back into Dexie.
 *
 * Inputs:  userId.
 * Outputs: The refreshed local user record, or the existing record on failure.
 * Failure Handling: Swallows errors and returns the existing record.
 * Idempotency: REQUIRED.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`).
 * Write Flow:      API GET → merge → Dexie put → wakeLocalWrite.
 * Sync Strategy:   Read-only server check; merged record then syncs.
 * Conflict Resolution: server response wins for invite state.
 */
export async function refreshSystemUserInviteStatus(userId: string): Promise<any | null> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(userId);
  if (!existing) return null;

  try {
    const apiResponse = await api.users.get(userId);
    const merged = mergeServerResponseIntoLocal(existing, apiResponse);
    await db.system_users.put(merged);
    wakeLocalWrite('system_users');
    return merged;
  } catch (err) {
    console.error('[systemUserService] refreshSystemUserInviteStatus failed:', err);
    return existing;
  }
}

/**
 * @process generateSystemUserInviteLink
 *
 * Steps:
 * 1. Load the system user from local Dexie.
 * 2. POST to the link generation endpoint.
 * 3. Merge the server response back into Dexie.
 *
 * Inputs:  userId, optional inviteMessage.
 * Outputs: { inviteUrl, expiresAt, user } or null.
 * Failure Handling: Rejects with the server error; callers surface it.
 * Idempotency: NOT idempotent — each call mints a new link.
 *
 * @localFirst
 * Source of Truth: local (Dexie `system_users`).
 * Write Flow:      API POST → merge → Dexie put → wakeLocalWrite.
 * Sync Strategy:   Link generation is the online step; merged record syncs.
 * Conflict Resolution: server response wins for invite state.
 */
export async function generateSystemUserInviteLink(
  userId: string,
  inviteMessage?: string,
): Promise<{ inviteUrl: string; expiresAt?: string } | null> {
  const db = await getSystemDb();
  const existing = await db.system_users.get(userId);
  if (!existing) return null;

  try {
    const response = await postGenerateInviteLink(userId, inviteMessage);
    const merged = mergeServerResponseIntoLocal(existing, response.user);
    await db.system_users.put(merged);
    wakeLocalWrite('system_users');
    return { inviteUrl: response.inviteUrl, expiresAt: response.expiresAt };
  } catch (err) {
    console.error('[systemUserService] generateSystemUserInviteLink failed:', err);
    throw new Error(toErrorMessage(err, 'Failed to generate invitation link'));
  }
}

// ============================================================
// Bulk Invitation Operations
// ============================================================

/**
 * @process bulkSendInvitations
 *
 * Steps:
 * 1. For each user id, call sendSystemUserInvitation.
 * 2. Collect results (success or error per user).
 *
 * Inputs:  userIds — array of system user ids to invite.
 * Outputs: Array of { userId, ok, user?, error? }.
 * Failure Handling: Individual failures do not abort the batch; each result
 *                   includes its own ok/error state.
 * Idempotency: REQUIRED — each call is idempotent per user.
 */
export async function bulkSendInvitations(
  userIds: string[],
  inviteMessage?: string,
): Promise<Array<{ userId: string; ok: boolean; user?: any; error?: string }>> {
  return Promise.all(
    userIds.map(async (userId) => {
      try {
        const user = await sendSystemUserInvitation(userId, inviteMessage);
        return { userId, ok: true, user };
      } catch (err) {
        return { userId, ok: false, error: toErrorMessage(err, 'Failed to send invitation') };
      }
    }),
  );
}

/**
 * @process bulkResendInvitations
 *
 * Steps:
 * 1. For each user, call resendSystemInvitation (the existing single-user resend).
 * 2. Collect results (success or error per user).
 *
 * Inputs:  users — array of system user objects whose invites should be resent.
 * Outputs: Array of BackgroundInviteResult.
 * Failure Handling: Individual failures do not abort the batch.
 * Idempotency: NOT idempotent — each call mints a fresh invite + email.
 */
export async function bulkResendInvitations(
  users: any[],
): Promise<BackgroundInviteResult[]> {
  return Promise.all(
    users.map((user) => resendSystemInvitation(user)),
  );
}
